/**
 * Date Utilities
 *
 * Pure utility functions for date parsing and formatting.
 * Consolidates date handling logic from ServiceFormSidebar.vue (parseDate)
 * and other components.
 */
// =============================================================================
// PARSING
// =============================================================================
/**
 * Parse various date formats to YYYY-MM-DD
 * Handles: YYYY-MM-DD, DD/MM/YYYY
 *
 * @example parseDate('2024-03-15') // '2024-03-15'
 * @example parseDate('15/03/2024') // '2024-03-15'
 */
export function parseDate(dateString) {
    if (!dateString)
        return null;
    const trimmed = dateString.trim();
    // Already in YYYY-MM-DD format
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
    }
    // Parse DD/MM/YYYY format
    const ddmmyyyy = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyy) {
        const [, day, month, year] = ddmmyyyy;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    // Parse MM/DD/YYYY format (less common, but handle it)
    const mmddyyyy = trimmed.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (mmddyyyy) {
        const [, month, day, year] = mmddyyyy;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return null;
}
/**
 * Format date to display format (DD/MM/YYYY)
 * @example formatDateDisplay('2024-03-15') // '15/03/2024'
 */
export function formatDateDisplay(dateString) {
    const parsed = parseDate(dateString);
    if (!parsed)
        return '';
    const [year, month, day] = parsed.split('-');
    return `${day}/${month}/${year}`;
}
/**
 * Format date to long Spanish format
 * @example formatDateLong('2024-03-15') // '15 de marzo de 2024'
 */
export function formatDateLong(dateString) {
    const parsed = parseDate(dateString);
    if (!parsed)
        return '';
    const date = new Date(parsed + 'T00:00:00');
    return date.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}
// =============================================================================
// COMPARISON
// =============================================================================
/**
 * Check if date1 is before date2
 * @example isDateBefore('2024-03-01', '2024-03-15') // true
 */
export function isDateBefore(date1, date2) {
    const d1 = parseDate(date1);
    const d2 = parseDate(date2);
    if (!d1 || !d2)
        return false;
    return new Date(d1) < new Date(d2);
}
/**
 * Check if date1 is after date2
 */
export function isDateAfter(date1, date2) {
    return isDateBefore(date2, date1);
}
/**
 * Check if date1 is same or before date2
 */
export function isDateSameOrBefore(date1, date2) {
    const d1 = parseDate(date1);
    const d2 = parseDate(date2);
    if (!d1 || !d2)
        return false;
    return new Date(d1) <= new Date(d2);
}
/**
 * Check if date1 is same or after date2
 */
export function isDateSameOrAfter(date1, date2) {
    return isDateSameOrBefore(date2, date1);
}
/**
 * Check if two dates are the same
 */
export function isDateSame(date1, date2) {
    const d1 = parseDate(date1);
    const d2 = parseDate(date2);
    return d1 === d2;
}
/**
 * Validate a date range (end >= start)
 * Used in AvailabilityConfigurationModal for override validation
 */
export function isValidDateRange(startDate, endDate) {
    return isDateSameOrBefore(startDate, endDate);
}
// =============================================================================
// VALIDATION
// =============================================================================
/**
 * Validate a date string
 */
export function validateDate(dateString) {
    if (!dateString || !dateString.trim()) {
        return { valid: false, error: 'La fecha es requerida' };
    }
    const parsed = parseDate(dateString);
    if (!parsed) {
        return { valid: false, error: 'Formato de fecha inválido' };
    }
    // Check if it's a valid date (not Feb 30, etc.)
    const date = new Date(parsed + 'T00:00:00');
    if (isNaN(date.getTime())) {
        return { valid: false, error: 'Fecha inválida' };
    }
    return { valid: true };
}
/**
 * Validate a date range
 */
export function validateDateRange(startDate, endDate) {
    const startValidation = validateDate(startDate);
    if (!startValidation.valid) {
        return { valid: false, error: `Fecha inicial: ${startValidation.error}` };
    }
    const endValidation = validateDate(endDate);
    if (!endValidation.valid) {
        return { valid: false, error: `Fecha final: ${endValidation.error}` };
    }
    if (!isValidDateRange(startDate, endDate)) {
        return {
            valid: false,
            error: 'La fecha final debe ser posterior o igual a la fecha inicial',
        };
    }
    return { valid: true };
}
// =============================================================================
// DATETIME DISPLAY
// =============================================================================
const CR_TIMEZONE = "America/Costa_Rica";
const WALL_CLOCK_DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?/;
const EXPLICIT_TZ_RE = /(?:Z|[+-]\d{2}:?\d{2})$/;
function pad2(value) {
    return String(value).padStart(2, "0");
}
function toTwelveHour(hour, minute) {
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${pad2(displayHour)}:${pad2(minute)} ${ampm}`;
}
function parseWallClockDateTime(value) {
    const match = value.match(WALL_CLOCK_DATETIME_RE);
    if (!match)
        return null;
    return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hour: Number(match[4]),
        minute: Number(match[5]),
    };
}
function getCostaRicaDateTimeParts(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime()))
        return null;
    const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: CR_TIMEZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    })
        .formatToParts(date)
        .reduce((acc, part) => {
        if (part.type !== "literal")
            acc[part.type] = part.value;
        return acc;
    }, {});
    return {
        year: Number(parts.year),
        month: Number(parts.month),
        day: Number(parts.day),
        hour: Number(parts.hour),
        minute: Number(parts.minute),
    };
}
function formatDateParts(parts) {
    return `${pad2(parts.day)}/${pad2(parts.month)}/${parts.year}`;
}
function getDisplayDateTimeParts(value, forceWallClock = false) {
    const raw = value instanceof Date ? value.toISOString() : String(value);
    if (forceWallClock || !EXPLICIT_TZ_RE.test(raw)) {
        return parseWallClockDateTime(raw);
    }
    return getCostaRicaDateTimeParts(raw);
}
/**
 * Format a datetime string for display using the Costa Rica timezone (UTC-6,
 * no DST) and a 24-hour clock. Returns "—" for empty/invalid input.
 *
 * @example formatServiceDatetime("2025-12-15T15:00:00Z") // "15/12/2025, 09:00"
 */
export function formatServiceDatetime(iso) {
    if (!iso)
        return "—";
    const date = new Date(iso);
    if (isNaN(date.getTime()))
        return "—";
    return date.toLocaleString("es-CR", {
        timeZone: CR_TIMEZONE,
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
}
export function formatCostaRicaDateTimeDisplay(value, fallback = "—") {
    if (!value)
        return fallback;
    const parts = getDisplayDateTimeParts(value);
    if (!parts)
        return fallback;
    return `${toTwelveHour(parts.hour, parts.minute)} - ${formatDateParts(parts)}`;
}
export function formatWallClockDateDisplay(value, fallback = "—") {
    if (!value)
        return fallback;
    const parts = getDisplayDateTimeParts(value, true);
    if (!parts)
        return fallback;
    return formatDateParts(parts);
}
export function formatWallClockTimeDisplay(value, fallback = "—") {
    if (!value)
        return fallback;
    const parts = getDisplayDateTimeParts(value, true);
    if (!parts)
        return fallback;
    return toTwelveHour(parts.hour, parts.minute);
}
export function formatWallClockTimeRangeDisplay(start, end, fallback = "—") {
    const startTime = formatWallClockTimeDisplay(start, "");
    const endTime = formatWallClockTimeDisplay(end, "");
    if (startTime && endTime)
        return `${startTime} – ${endTime}`;
    return startTime || endTime || fallback;
}
// =============================================================================
// UTILITIES
// =============================================================================
/**
 * Get today's date in YYYY-MM-DD format
 */
export function today() {
    return new Date().toISOString().split('T')[0];
}
/**
 * Add days to a date
 * @example addDays('2024-03-15', 7) // '2024-03-22'
 */
export function addDays(dateString, days) {
    const parsed = parseDate(dateString);
    if (!parsed)
        return today();
    const date = new Date(parsed + 'T00:00:00');
    date.setDate(date.getDate() + days);
    return date.toISOString().split('T')[0];
}
/**
 * Get the difference in days between two dates
 * @example daysBetween('2024-03-01', '2024-03-15') // 14
 */
export function daysBetween(date1, date2) {
    const d1 = parseDate(date1);
    const d2 = parseDate(date2);
    if (!d1 || !d2)
        return 0;
    const ms = Math.abs(new Date(d2).getTime() - new Date(d1).getTime());
    return Math.floor(ms / (1000 * 60 * 60 * 24));
}
/**
 * Get day of week for a date (0 = Monday, 6 = Sunday)
 * @example getDayOfWeek('2024-03-15') // 4 (Friday)
 */
export function getDayOfWeek(dateString) {
    const parsed = parseDate(dateString);
    if (!parsed)
        return 0;
    const date = new Date(parsed + 'T00:00:00');
    // JavaScript: 0 = Sunday, but we want 0 = Monday
    const jsDay = date.getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
}
/**
 * Check if a date is in the past
 */
export function isDateInPast(dateString) {
    return isDateBefore(dateString, today());
}
/**
 * Check if a date is in the future
 */
export function isDateInFuture(dateString) {
    return isDateAfter(dateString, today());
}
/**
 * Check if a date is today
 */
export function isToday(dateString) {
    return isDateSame(dateString, today());
}
